import { useEffect, useRef, useState } from "react";
import type { AskResult, CheckAttempt, AnsweredTopic, Turn } from "@howcomet/shared";
import { CATEGORIES, invitationFor } from "@howcomet/shared";
import { api, ApiError } from "../api";
import { BigButton } from "../components/BigButton";
import { Bubble } from "../components/Bubble";
import { Check, type CheckResult } from "../shells/registry";
import { Thinking } from "../components/Thinking";
import { FigureView } from "../figures/registry";
import { canListen, listen, stopSpeaking, type Listener } from "../speech";
import { usePublishedHeight } from "../bottom-bars";

type Stage =
  | "idle"
  | "thinking"
  | "answer"
  | "check"
  | "done"
  | "blocked"
  | "enough"
  | "error";

/**
 * One continuous exploration, not a series of isolated questions.
 *
 * The brief's bet is that the product is what happens *after* the answer. A
 * child who gets one answer and stops has used a search engine; a child who
 * asks "how about squares?" and is understood is doing the thing. So earlier
 * turns stay on the page, the next question inherits them, and finishing a
 * check offers somewhere deeper to go.
 */
export function AskScreen({
  active,
  onAwarded,
  autoRead,
}: {
  /**
   * True while this tab is showing. The screen stays mounted when it is not,
   * which is the whole point — a question takes about ten seconds and a child
   * who wanders off to their sky must come back to the answer, not an empty
   * chat.
   */
  active: boolean;
  onAwarded: (message: string) => void;
  autoRead: boolean;
}) {
  // Published so the toast can clear it, and so a page scrolled to the bottom
  // knows how much of the screen it cannot use.
  const composerRef = usePublishedHeight<HTMLDivElement>("--composer-h");

  const [stage, setStage] = useState<Stage>("idle");
  const [text, setText] = useState("");
  /** Exchanges already finished, oldest first. Scrolls back up the page. */
  const [turns, setTurns] = useState<Turn[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [topic, setTopic] = useState<AnsweredTopic | null>(null);
  const [attempt, setAttempt] = useState<CheckAttempt | null>(null);
  const [message, setMessage] = useState("");
  const [parentNotified, setParentNotified] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [collecting, setCollecting] = useState(false);
  const [listening, setListening] = useState(false);
  const listenerRef = useRef<Listener | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const liveRef = useRef<HTMLDivElement>(null);
  const checkRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api
      .suggestions()
      .then((r) => setSuggestions(r.suggestions))
      .catch(() => setSuggestions([]));

    // Pick the conversation back up on reload. The server decides whether it's
    // still fresh enough to resume — coming back tomorrow starts clean.
    api
      .thread()
      .then((r) => {
        if (!r.threadId || r.turns.length === 0) return;
        setThreadId(r.threadId);
        setTurns(r.turns);
      })
      .catch(() => {});
  }, []);

  /** Move to whatever just appeared — it's usually below the fold. */
  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
    requestAnimationFrame(() => ref.current?.scrollIntoView({ block: "start" }));
  };

  useEffect(() => {
    if (!active) return;
    if (stage === "check") scrollTo(checkRef);
    if (stage === "answer") scrollTo(liveRef);
  }, [stage, active]);

  // Reading aloud follows the child, not the clock: leaving the tab stops it.
  useEffect(() => {
    if (!active) stopSpeaking();
  }, [active]);

  const ask = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed) return;

    stopSpeaking();
    setText("");

    /*
      Put the iPad's keyboard away.
      
      On iOS the on-screen keyboard does not close by itself when a form is
      submitted — it stays up, holding the whole layout shoved upward, and the
      child watches a squeezed strip of screen instead of their question being
      worked on. Blurring the field is what dismisses it.
    */
    inputRef.current?.blur();

    /*
      Fold the exchange on screen into the history before the new one replaces
      it. This belongs here and not at the call sites: the composer, the
      follow-up buttons, the suggestion chips and the microphone all arrive
      through this function, and only the follow-up buttons remembered to do it
      — so typing your own follow-up made the previous turn vanish.

      It also settles a related oddity. Asking again without finishing the first
      check advances the server-side depth, so the header could read "2 deep"
      above a single visible question. Now the turn it is counting is on screen.
    */
    bank();
    setStage("thinking");

    try {
      const result: AskResult = await api.ask({
        text: trimmed,
        ...(threadId ? { threadId } : {}),
      });

      switch (result.status) {
        case "answered":
          setTopic(result.topic);
          setAttempt(result.attempt);
          setThreadId(result.threadId);
          setMessage(
            result.gatesNextQuestion
              ? "That looked like homework. Finish the check and then you can ask something new."
              : "",
          );
          setStage("answer");
          break;

        case "finish_check":
          setTopic(result.topic);
          setAttempt(result.attempt);
          setMessage(result.message);
          setStage("answer");
          break;

        case "blocked":
          setMessage(result.message);
          setParentNotified(result.parentNotified);
          setStage("blocked");
          break;

        /*
          Not an error, and styled so it doesn't read like one. The child has
          been curious all day, which is the behaviour this whole product is
          trying to cause — the worst possible response is a red box.
        */
        case "enough_for_today":
          setMessage(result.message);
          setStage("enough");
          break;

        case "unclear":
          setMessage(result.message);
          setSuggestions(result.suggestions);
          setStage("error");
          break;

        /*
          A status this build has never heard of.

          The iPad ships separately from the service, so a tablet can be weeks
          behind what the server answers with — and every branch above sets the
          stage, which means an unmatched status left `thinking` on screen
          spinning with no way out. A child cannot be given a dead end because
          we shipped two things on different days.

          `enough_for_today` was the first one to find this. It will not be the
          last, so the fallback says something true of any of them: the server
          replied with something, and it wasn't an answer.
        */
        default:
          setMessage("Something changed on my end. Try again in a moment.");
          setStage("error");
          break;
      }
    } catch (error) {
      setMessage(
        error instanceof ApiError ? error.message : "Something went wrong. Try again in a moment.",
      );
      setStage("error");
    }
  };

  const collect = async (result: CheckResult) => {
    if (!attempt || collecting) return;
    setCollecting(true);
    try {
      const outcome = await api.completeCheck(attempt.attemptId, result);
      onAwarded(outcome.message);
      setStage("done");
    } catch (error) {
      if (error instanceof ApiError && error.code === "already_claimed") {
        setStage("done");
      } else {
        onAwarded(error instanceof ApiError ? error.message : "Couldn't collect that one.");
      }
    } finally {
      setCollecting(false);
    }
  };

  /** Fold the finished exchange into the history and clear the stage. */
  const bank = () => {
    if (topic) {
      setTurns((t) => [
        ...t,
        {
          question: topic.question,
          answer: topic.answer,
          starName: topic.starName,
          category: topic.category,
          topicId: topic.topicId,
          depth: topic.depth,
        },
      ]);
    }
    setTopic(null);
    setAttempt(null);
    setMessage("");
    setStage("idle");
  };

  const startFresh = async () => {
    stopSpeaking();
    // Always call, even with no thread id in hand. The server closes everything
    // open for this child, and skipping the call when local state happened to
    // be empty is precisely how a finished exploration came back.
    await api.closeThread().catch(() => {});
    setThreadId(null);
    setTurns([]);
    setTopic(null);
    setAttempt(null);
    setMessage("");
    setStage("idle");
  };

  const startListening = () => {
    if (listening) return;
    setListening(true);
    listenerRef.current = listen({
      onPartial: setText,
      onFinal: (final) => {
        setListening(false);
        void ask(final);
      },
      onError: (msg) => {
        setListening(false);
        setMessage(msg);
        setStage("error");
      },
    });
  };

  const stopListening = () => {
    listenerRef.current?.stop();
    listenerRef.current = null;
    setListening(false);
  };

  const category = topic ? CATEGORIES[topic.category] : null;
  const inThread = turns.length > 0 || topic !== null;

  return (
    <div className="pb-44">
      {/* Earlier turns, quieter than the live one but still readable — a child
          scrolling up should be able to see the path they took. */}
      {turns.map((turn, index) => (
        <div key={`${turn.topicId}-${index}`} className="mb-5 border-l-2 border-cream/10 pl-4">
          <div
            className="disp mb-1 text-[13px] uppercase tracking-wide"
            style={{ color: CATEGORIES[turn.category].color }}
          >
            {CATEGORIES[turn.category].emoji} {turn.starName}
          </div>
          <p className="disp text-[19px] leading-snug text-cream/85">{turn.question}</p>
          <p className="mt-1 text-[16px] leading-relaxed text-cream/55">{turn.answer}</p>
        </div>
      ))}

      {stage === "idle" && turns.length === 0 && (
        <div className="rise">
          <p className="disp text-[30px] leading-tight text-cream">What do you want to know?</p>
          <p className="mt-2 text-cream/60">
            Ask anything. Every question you finish becomes a star.
          </p>
        </div>
      )}

      {stage === "thinking" && <Thinking />}

      {stage === "blocked" && (
        <div className="rise">
          <div className="rounded-3xl border-2 border-amber bg-panel p-5">
            <p className="disp text-[22px] text-amber">Not this one</p>
            <p className="mt-2 text-[18px] leading-relaxed text-cream">{message}</p>
            {parentNotified && (
              <p className="mt-3 text-[16px] font-bold text-amber">
                A grown-up will see that you asked this, so you can talk about it together.
              </p>
            )}
          </div>
        </div>
      )}

      {stage === "enough" && (
        <div className="rise">
          <div className="rounded-3xl border-2 border-gold/50 bg-panel p-5 text-center">
            <div className="text-[40px]">🌙</div>
            <p className="disp mt-1 text-[22px] text-gold">That's a lot of questions</p>
            <p className="mt-2 text-[18px] leading-relaxed text-cream">{message}</p>
          </div>
        </div>
      )}

      {stage === "error" && (
        <div className="rise">
          <Bubble text={message} />
        </div>
      )}

      {topic && (stage === "answer" || stage === "check" || stage === "done") && (
        <div ref={liveRef} className="rise scroll-mt-4">
          {message && (
            <div className="mb-4 rounded-2xl bg-amber/20 px-4 py-3 text-[15px] font-bold text-amber">
              {message}
            </div>
          )}

          <div className="disp mb-2 flex items-center gap-2 text-[15px]">
            <span style={{ color: category?.color }}>
              {category?.emoji} {category?.name}
            </span>
            {topic.depth > 1 && (
              <span className="rounded-full bg-cyan/15 px-2 py-0.5 text-[12px] text-cyan">
                {topic.depth} deep
              </span>
            )}
          </div>
          <h1 className="disp mb-3 text-[26px] leading-tight text-cream">{topic.question}</h1>

          {/* Answer first, always. The hook comes after — teaching is the
              reward, never the toll booth. */}
          <Bubble text={topic.answer} autoRead={autoRead} />

          {/* After the words, never instead of them. The answer has to stand on
              its own for a child who can't see it, or who is being read to. */}
          <FigureView figure={topic.figure} />

          {/*
            The offer, in three parts, and the split is deliberate.

            The dynamic line is the model's hook — it names the specific
            interesting bit of THIS answer, which is what makes the offer feel
            addressed to the child rather than bolted on. The second line comes
            from the shell registry, so a child knows what kind of thing they're
            about to be asked without a mystery box. The button never varies by
            question: it is a promise, in the same place every time, and it
            names the prize rather than the task — "show me why" described the
            mechanism and buried the reason to tap.
          */}
          {stage === "answer" && (
            <div className="mt-5">
              <p className="disp text-[19px] text-cream">{topic.hook}</p>
              <p className="mb-3 mt-1 text-[14px] text-cream/50">
                {invitationFor(attempt?.spec.type)}
              </p>
              <BigButton onClick={() => setStage("check")} color="pink">
                {topic.known ? "✨ Brighten my star" : "⭐ Collect my star"}
              </BigButton>
              {topic.known && (
                <p className="mt-3 text-[14px] text-cream/45">
                  You already have “{topic.starName}” — finishing again brightens it.
                </p>
              )}
            </div>
          )}

          {stage === "check" && attempt && (
            <div ref={checkRef} className="scroll-mt-4">
              <Check spec={attempt.spec} onDone={collect} collecting={collecting} />
            </div>
          )}

          {stage === "done" && (
            <div className="pop mt-6">
              <div className="text-center">
                <div className="text-[50px]">🌟</div>
                <p className="disp mt-2 text-[24px] text-gold">
                  “{topic.starName}” is shining in your sky
                </p>
              </div>

              {/*
                The whole point of the product sits here. A child who stops at
                one answer has used a search box; the offer to go deeper — with
                actual questions, not a blank box — is what turns it into
                learning. Stardust grows with depth, but only on the check, so
                there's nothing to farm by asking.
              */}
              {topic.followUps.length > 0 && (
                <div className="mt-7">
                  <p className="disp text-[20px] text-cream">Want to dig deeper?</p>
                  <p className="mb-3 mt-1 text-[15px] text-cream/55">
                    The further you go, the more stardust each star is worth.
                  </p>
                  <div className="flex flex-col gap-2">
                    {topic.followUps.map((followUp) => (
                      <button
                        key={followUp}
                        onClick={() => void ask(followUp)}
                        className="kidbtn flex items-center gap-3 rounded-2xl bg-panel2 px-5 py-4 text-left text-[17px] font-bold text-cream"
                      >
                        <span className="text-[20px]">🔎</span>
                        {followUp}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={bank}
                className="kidbtn disp mt-5 w-full rounded-full bg-panel px-6 py-3 text-[17px] text-cream"
              >
                Ask my own question
              </button>
            </div>
          )}
        </div>
      )}

      {/* Suggestion chips do real work: they're how a six-year-old who can't
          spell "volcano" starts, and how the product shows what it's good at. */}
      {(stage === "idle" || stage === "blocked" || stage === "error") &&
        suggestions.length > 0 &&
        turns.length === 0 && (
          <div className="mt-8">
            <p className="disp mb-3 text-[13px] uppercase tracking-wider text-cream/45">Or try</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => void ask(suggestion)}
                  className="kidbtn rounded-full bg-panel px-4 py-2 text-[14px] font-bold text-cream/85"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

      {/* Leaving the thread is explicit and always available, so a child is
          never stuck inside a topic they've lost interest in. */}
      {inThread && stage !== "thinking" && (
        <button
          onClick={() => void startFresh()}
          className="kidbtn mt-8 w-full rounded-full border border-cream/15 py-3 text-[15px] font-bold text-cream/50"
        >
          ✨ I want to explore something new
        </button>
      )}

      {/* One input. One hold-to-talk button. Nothing else — principle P1. */}
      {/*
        Sits on top of the tab bar, wherever the tab bar actually ends up. The
        fallback matters on the first paint and on any browser without
        ResizeObserver; it is the old iPad measurement, which is right there and
        wrong on a phone — hence the variable.
      */}
      <div
        ref={composerRef}
        className="fixed inset-x-0 z-30 flex justify-center px-4"
        style={{ bottom: "calc(var(--tabbar-h, 92px) + 0.5rem)" }}
      >
        <div className="w-full max-w-[828px]">
          <div className="flex items-center gap-2 rounded-full bg-panel2 p-2 shadow-[0_8px_30px_rgba(0,0,0,.45)]">
            {canListen() && (
              <button
                onPointerDown={(e) => {
                  /*
                    Capture the pointer for the whole hold.

                    Without this the button relied on onPointerLeave to know the
                    press had ended — so a finger drifting a few millimetres off
                    a 52px circle, which is what happens when a seven-year-old
                    holds something and talks, cancelled the recording. It read
                    as a button that had to be hit exactly right and then kept
                    perfectly still. Capture means the press ends when the
                    finger lifts, wherever it has wandered to.
                  */
                  e.currentTarget.setPointerCapture(e.pointerId);
                  startListening();
                }}
                onPointerUp={stopListening}
                onPointerCancel={stopListening}
                aria-label="Hold to talk"
                className={`kidbtn flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-pink text-[28px] ${
                  listening ? "pulse-mic" : ""
                }`}
              >
                <span aria-hidden className="pointer-events-none select-none">
                  🎤
                </span>
              </button>
            )}
            <input
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void ask(text)}
              placeholder={
                listening ? "Listening…" : inThread ? "Ask more about this…" : "Ask me anything…"
              }
              enterKeyHint="send"
              autoComplete="off"
              className="min-w-0 flex-1 bg-transparent text-[18px] text-cream outline-none placeholder:text-cream/40"
            />
            <button
              onClick={() => void ask(text)}
              disabled={!text.trim()}
              aria-label="Ask"
              className="kidbtn flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full bg-gold text-[24px] text-night disabled:opacity-40"
            >
              →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
